import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";

/**
 * Dropdown menu for sharing, exporting, and clearing the chart.
 * @prop locked - When true, disables items that require edit mode (Clear).
 * @prop clearPending - When true, the "Clear" item shows a confirmation prompt.
 * @fires copy-link - When "Copy Shareable Link" is selected.
 * @fires download-png - When "Download Graph (PNG)" is selected.
 * @fires download-svg - When "Download Graph (SVG)" is selected.
 * @fires clear-requested - When "Clear" is selected.
 */
@customElement("bab-action-menu")
export class BabActionMenu extends LitElement {
  static styles = css`
    :host {
      position: relative;
      display: inline-block;
    }

    .menu-toggle {
      display: flex;
      align-items: center;
      gap: 6px;
      height: 30px;
      padding: 0 12px;
      background: var(--bab-button-secondary-bg, #fff);
      color: var(--bab-button-secondary-text, #4a90e2);
      border: var(--bab-border-width, 1.5px) solid var(--bab-border);
      border-radius: var(--bab-radius, 12px);
      font-size: 0.8rem;
      font-weight: var(--bab-font-weight, 600);
      font-family: inherit;
      cursor: pointer;
      white-space: nowrap;
      transition: all 0.2s;
    }

    .menu-toggle:hover {
      background: color-mix(
        in srgb,
        var(--bab-button-secondary-bg, #fff) 85%,
        var(--bab-button-secondary-text, #4a90e2) 15%
      );
    }

    .menu-toggle:focus-visible {
      outline: 2px solid var(--bab-accent, #4a90e2);
      outline-offset: 2px;
    }

    .menu-toggle svg {
      width: 14px;
      height: 14px;
      transition: transform 0.2s;
    }

    .menu-toggle[aria-expanded="true"] svg {
      transform: rotate(180deg);
    }

    .action-menu {
      position: absolute;
      top: calc(100% + 4px);
      right: 0;
      z-index: 100;
      min-width: 210px;
      background: var(--bab-card-bg);
      border: var(--bab-border-width, 1.5px) solid var(--bab-border);
      border-radius: var(--bab-radius, 12px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      padding: 4px;
      opacity: 0;
      visibility: hidden;
      transform: translateY(-8px);
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .action-menu--visible {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
    }

    .action-menu-item {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      padding: 8px 12px;
      border: none;
      background: transparent;
      color: var(--bab-text);
      font-size: 0.85rem;
      font-weight: 500;
      text-align: left;
      cursor: pointer;
      border-radius: 4px;
      transition: background 0.15s;
    }

    .action-menu-item:hover:not(:disabled) {
      background: var(--bab-bg, #f0f4f8);
    }

    .action-menu-item:disabled {
      color: var(--bab-text-secondary);
      cursor: not-allowed;
      opacity: 0.5;
    }

    .action-menu-item--warn:not(:disabled) {
      color: #f59e0b;
    }

    .action-menu-item svg {
      width: 16px;
      height: 16px;
      color: var(--bab-text-secondary);
      flex-shrink: 0;
    }
  `;

  @property({ type: Boolean })
  locked = false;

  @property({ type: Boolean })
  clearPending = false;

  @state()
  private _open = false;

  private _onWindowClick = (e: MouseEvent) => {
    const path = e.composedPath();
    if (this._open && !path.includes(this)) {
      this._open = false;
    }
  };

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("click", this._onWindowClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("click", this._onWindowClick);
  }

  private _onKeyDown(e: KeyboardEvent) {
    if (e.key === "Escape") {
      this._open = false;
      this.shadowRoot?.querySelector<HTMLElement>(".menu-toggle")?.focus();
    }
  }

  private _toggle(e: Event) {
    e.stopPropagation();
    this._open = !this._open;
  }

  private _dispatchAndClose(name: string) {
    this.dispatchEvent(new CustomEvent(name));
    this._open = false;
  }

  private _onClearClick() {
    const wasPending = this.clearPending;
    this.dispatchEvent(new CustomEvent("clear-requested"));
    if (wasPending) {
      this._open = false;
    }
  }

  render() {
    return html`
      <button
        class="menu-toggle"
        @click="${this._toggle}"
        aria-haspopup="true"
        aria-expanded="${this._open}"
        aria-label="More actions"
      >
        Actions
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>
      <div class="action-menu ${this._open ? "action-menu--visible" : ""}" role="menu" @keydown="${this._onKeyDown}">
        <button class="action-menu-item" role="menuitem" @click="${() => this._dispatchAndClose("download-png")}">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          Download Graph (PNG)
        </button>
        <button class="action-menu-item" role="menuitem" @click="${() => this._dispatchAndClose("download-svg")}">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          Download Graph (SVG)
        </button>
        <button class="action-menu-item" role="menuitem" @click="${() => this._dispatchAndClose("copy-link")}">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
          </svg>
          Copy Shareable Link
        </button>
        <button
          class="action-menu-item ${this.clearPending ? "action-menu-item--warn" : ""}"
          role="menuitem"
          ?disabled="${this.locked}"
          @click="${this._onClearClick}"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
          ${this.clearPending ? "Confirm clear?" : "Clear"}
        </button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bab-action-menu": BabActionMenu;
  }
}
